/**
 * ShadowedLightVolume — participating media that sample LIGHT VISIBILITY
 * (shader additions wave, 2026-09-15): sun shafts through buildings and trees,
 * lamp cones in fog, a headlight through dust. A local box volume is
 * ray-marched per pixel; at every march point the scene's lights are
 * evaluated WITH their shadow maps (the sun's CSM included) and a
 * Henyey–Greenstein phase, and the segment is integrated exactly.
 *
 * WHAT three ALREADY HAD, AND WHY THIS SUBCLASSES IT. r185's
 * `VolumeNodeMaterial` + `VolumetricLightingModel` already march a volume and
 * evaluate the standard lights loop at each step with
 * `context.shadowPositionWorld` set to the march point — which is exactly how
 * a light's `ShadowNode` (CSM too) gets sampled off the surface. Two things it
 * cannot do for us: its `direct()` SKIPS every light whose `distance` is
 * undefined — i.e. the SUN, the one shadow-caster this world has — and it has
 * no phase function. `ShadowedVolumeLightingModel` keeps the same march and
 * admits directionals with HG phase. It also does NOT multiply by
 * `lightNode.shadowNode` a second time: `AnalyticLightNode.setupShadow` has
 * already folded the shadow into the `lightColor` it hands `direct()`.
 *
 * THE INTEGRAL (per segment of length ds, extinction σt, source q):
 *   T_seg = e^(−σt·ds)          ΔC = T_before · q · (1 − T_seg) / σt
 *   T_after = T_before · T_seg   (limit → ds as σt → 0)
 * exact for constant coefficients along the segment.
 *   q = σs · ( Σ_lights lightColor·shadow·p(µ) · tint  +  ambient )
 *   p(µ) = (1 − g²) / (4π (1 + g² − 2gµ)^{3/2}),   µ = L·D
 * with L toward the light and D the camera ray (away from the camera): g > 0
 * scatters forward, so the sun in front of you glows through the haze.
 *
 * COMPOSITION. The material writes PREMULTIPLIED scattering in rgb and
 * (1 − T) in alpha (`setupOutput`), blended Normal — so the background is
 * attenuated by the medium's own transmittance. The march runs from the box
 * ENTRY along the camera ray to the box exit (this fragment: BackSide, no
 * depth test) or to the opaque scene depth, whichever is nearer — never
 * outside the box, never behind geometry.
 *
 * FRAME. `positionWorld` / `cameraPosition` here are RENDER space (the FP64
 * rebase: scene.position = −origin), so the box bounds the shader is handed
 * must be render space too: `handle.setBoxWorld(center, half)` with the mesh's
 * world position, not its absolute one. Atlas does that per frame.
 *
 * SCOPE. Shadow maps give geometric visibility; the medium does not shadow
 * ITSELF along the light path (lightT = 1) — a first tier, stated as such.
 * Rect-area lights are not scattered (three's LTC volume term is private).
 */
import * as THREE from 'three/webgpu';
import {
  float, vec3, vec4, uniform, property, Loop, select,
  positionWorld, cameraPosition, cameraViewMatrix, cameraNear, cameraFar,
  perspectiveDepthToViewZ, viewportDepthTexture, screenUV, mx_noise_float, hash, time,
} from 'three/tsl';

export interface ShadowedLightVolumeConfig {
  /** Box extents in metres (default 60 × 24 × 60). */
  size?: [number, number, number];
  /** Extinction σt per metre at full density (default 0.02 ≈ 200 m visibility). */
  density?: number;
  /** Scattering fraction σs/σt (default 0.9). */
  albedo?: number;
  /** HG anisotropy, −1..1 (default 0.55, forward). */
  g?: number;
  /** Isotropic sky in-scatter (radiance), default a faint blue. */
  ambient?: [number, number, number];
  /** Tint on the scattered light (default white). */
  color?: [number, number, number];
  /** Density e-folds every this many metres above the box floor (default 12). */
  heightFalloffM?: number;
  /** Metres per noise period (default 9) and how much of the density is noise, 0..1 (default 0.55). */
  noiseScaleM?: number;
  noiseAmount?: number;
  /** March steps per pixel (default 24). */
  steps?: number;
  /** Stop the march at the opaque scene depth (default true). */
  useSceneDepth?: boolean;
}

export interface ShadowedLightVolumeUniforms {
  density: { value: number };
  albedo: { value: number };
  g: { value: number };
  ambient: { value: THREE.Vector3 };
  color: { value: THREE.Vector3 };
  heightFalloff: { value: number };
  noiseScale: { value: number };
  noiseAmount: { value: number };
  wind: { value: THREE.Vector3 };
  boxMin: { value: THREE.Vector3 };
  boxMax: { value: THREE.Vector3 };
  steps: { value: number };
}

export interface ShadowedLightVolumeHandle {
  mesh: THREE.Mesh;
  material: ShadowedVolumeMaterial;
  uniforms: ShadowedLightVolumeUniforms;
  /** Render-space box (centre + half extents); call after moving the mesh. */
  setBoxWorld(center: THREE.Vector3, half: THREE.Vector3): void;
}

// ── CPU mirrors (the harness pins these) ──────────────────────────────────

/** Henyey–Greenstein phase for µ = L·D (L toward the light, D the camera ray). */
export function hgPhaseCpu(mu: number, g: number): number {
  const gg = g * g;
  const d = 1 + gg - 2 * g * Math.max(-1, Math.min(1, mu));
  return (1 - gg) / (4 * Math.PI * Math.pow(Math.max(1e-6, d), 1.5));
}

/** One segment of the front-to-back integral: returns the added radiance and the transmittance after it. */
export function segmentScatterCpu(sigmaT: number, ds: number, q: number, tBefore: number): { dC: number; tAfter: number } {
  const st = Math.max(0, sigmaT);
  const tSeg = Math.exp(-st * ds);
  const integral = st < 1e-6 ? ds : (1 - tSeg) / st;
  return { dC: tBefore * q * integral, tAfter: tBefore * tSeg };
}

/** Height density factor: 1 at and below the floor, e-folding every `falloffM` above it. */
export function heightDensityCpu(y: number, floorY: number, falloffM: number): number {
  return Math.exp(-Math.max(0, y - floorY) / Math.max(1e-3, falloffM));
}

// ── The lighting model ────────────────────────────────────────────────────

const scatteredLight = property('vec3', 'volumeScatteredLight');
const outgoingRayLight = property('vec3', 'volumeOutgoingLight');
const rayTransmittance = property('float', 'volumeTransmittance');

interface ModelUniforms {
  g: ReturnType<typeof uniform>;
}

export class ShadowedVolumeLightingModel extends THREE.LightingModel {
  constructor(private readonly u: ModelUniforms, private readonly densityAt: (p: unknown) => unknown, private readonly vol: {
    density: ReturnType<typeof uniform>; albedo: ReturnType<typeof uniform>; ambient: ReturnType<typeof uniform>;
    color: ReturnType<typeof uniform>; boxMin: ReturnType<typeof uniform>; boxMax: ReturnType<typeof uniform>;
    steps: ReturnType<typeof uniform>; useSceneDepth: boolean;
  }) { super(); }

  start(builder: any): void {
    const { material } = builder;
    const cam = cameraPosition;
    const toFrag = positionWorld.sub(cam);
    const fragDist = toFrag.length().toVar();
    const rayDir = toFrag.div(fragDist.max(1e-4)).toVar();
    // Slab test against the render-space box: march only INSIDE it.
    const inv = vec3(1).div(rayDir.abs().max(1e-5)).mul(rayDir.sign());
    const t0 = this.vol.boxMin.sub(cam).mul(inv), t1 = this.vol.boxMax.sub(cam).mul(inv);
    const tNear = t0.min(t1), tFar = t0.max(t1);
    const tEntry = tNear.x.max(tNear.y).max(tNear.z).max(0).toVar();
    let tExit = tFar.x.min(tFar.y).min(tFar.z).min(fragDist).toVar();
    if (this.vol.useSceneDepth) {
      // The opaque depth at this pixel as a distance along OUR ray.
      const depth = viewportDepthTexture(screenUV).r;
      const viewZ = perspectiveDepthToViewZ(depth, cameraNear, cameraFar); // negative metres
      const dirView = cameraViewMatrix.mul(vec4(rayDir, float(0))).xyz;
      const tScene = viewZ.negate().div(dirView.z.negate().max(1e-4));
      tExit = tExit.min(tScene).toVar();
    }
    const span = tExit.sub(tEntry).max(0).toVar();
    const stepSize = span.div(float(this.vol.steps)).toVar();
    const dist = tEntry.toVar();
    if (material.offsetNode) dist.addAssign(material.offsetNode.mul(stepSize)); // jitter against banding
    const T = float(1).toVar();
    outgoingRayLight.assign(vec3(0));

    Loop(this.vol.steps, () => {
      const p = cam.add(rayDir.mul(dist));
      const pView = cameraViewMatrix.mul(vec4(p, 1)).xyz;
      builder.context.positionWorld = p;
      builder.context.shadowPositionWorld = p;
      builder.context.positionView = pView;
      scatteredLight.assign(vec3(0));
      // Every light, shadow-sampled at THIS point, into `scatteredLight` (direct()).
      super.start(builder);
      const sigmaT = this.vol.density.mul(float(this.densityAt(p) as never)).toVar();
      const sigmaS = sigmaT.mul(this.vol.albedo);
      const q = sigmaS.mul(scatteredLight.mul(this.vol.color).add(this.vol.ambient));
      const tSeg = sigmaT.negate().mul(stepSize).exp();
      const integral = select(sigmaT.lessThan(1e-5), stepSize, float(1).sub(tSeg).div(sigmaT.max(1e-5)));
      outgoingRayLight.addAssign(q.mul(T).mul(integral));
      T.mulAssign(tSeg);
      dist.addAssign(stepSize);
    });
    rayTransmittance.assign(T);
  }

  direct({ lightNode, lightColor, lightDirection }: any, builder: any): void {
    if (lightNode?.isAnalyticLightNode !== true) return;
    // `lightColor` already carries the light's shadow (AnalyticLightNode.setupShadow).
    const light = vec3(lightColor).toVar();
    if (lightDirection) {
      // µ = L·D with D the camera ray in view space (camera at the origin).
      const d = builder.context.positionView;
      const mu = vec3(lightDirection).dot(vec3(d).normalize());
      const g = this.u.g;
      const gg = g.mul(g);
      const den = float(1).add(gg).sub(g.mul(2).mul(mu)).max(1e-4);
      const phase = float(1).sub(gg).div(den.pow(1.5).mul(4 * Math.PI));
      light.mulAssign(phase);
    }
    scatteredLight.addAssign(light);
  }

  finish(builder: any): void {
    builder.context.outgoingLight.assign(outgoingRayLight);
  }
}

// ── The material ──────────────────────────────────────────────────────────

export class ShadowedVolumeMaterial extends THREE.VolumeNodeMaterial {
  model: ShadowedVolumeLightingModel | null = null;
  setupLightingModel(): any { return this.model; }
  /** Premultiplied scattering + (1 − T) coverage, blended Normal. */
  setupOutput(builder: any, outputNode: any): any {
    return super.setupOutput(builder, vec4(vec3(outputNode.rgb), float(1).sub(rayTransmittance)));
  }
}

export function createShadowedLightVolume(config?: ShadowedLightVolumeConfig): ShadowedLightVolumeHandle {
  const size = config?.size ?? [60, 24, 60];
  const amb = config?.ambient ?? [0.03, 0.035, 0.045];
  const col = config?.color ?? [1, 1, 1];
  const u = {
    density: uniform(config?.density ?? 0.02),
    albedo: uniform(config?.albedo ?? 0.9),
    g: uniform(config?.g ?? 0.55),
    ambient: uniform(new THREE.Vector3(amb[0], amb[1], amb[2])),
    color: uniform(new THREE.Vector3(col[0], col[1], col[2])),
    heightFalloff: uniform(config?.heightFalloffM ?? 12),
    noiseScale: uniform(config?.noiseScaleM ?? 9),
    noiseAmount: uniform(config?.noiseAmount ?? 0.55),
    wind: uniform(new THREE.Vector3(0, 0, 0)),
    boxMin: uniform(new THREE.Vector3(-size[0] / 2, -size[1] / 2, -size[2] / 2)),
    boxMax: uniform(new THREE.Vector3(size[0] / 2, size[1] / 2, size[2] / 2)),
    steps: uniform(config?.steps ?? 24, 'int'),
  };
  // Density 0..1 at a render-space point: inside the box, falling off with
  // height above the box floor, broken up by drifting noise.
  const densityAt = (p: unknown): unknown => {
    const pp = vec3(p as never);
    const edge = float(1.5);
    const inX = pp.x.sub(u.boxMin.x).min(u.boxMax.x.sub(pp.x)).div(edge).clamp(0, 1);
    const inY = pp.y.sub(u.boxMin.y).min(u.boxMax.y.sub(pp.y)).div(edge).clamp(0, 1);
    const inZ = pp.z.sub(u.boxMin.z).min(u.boxMax.z.sub(pp.z)).div(edge).clamp(0, 1);
    const inside = inX.mul(inY).mul(inZ);
    const h = pp.y.sub(u.boxMin.y).max(0).div(u.heightFalloff).negate().exp();
    const n = mx_noise_float(pp.div(u.noiseScale).add(u.wind)).mul(0.5).add(0.5);
    return inside.mul(h).mul(float(1).sub(u.noiseAmount).add(u.noiseAmount.mul(n)));
  };

  const material = new ShadowedVolumeMaterial();
  material.name = 'ShadowedLightVolume';
  material.model = new ShadowedVolumeLightingModel({ g: u.g }, densityAt, {
    density: u.density, albedo: u.albedo, ambient: u.ambient, color: u.color,
    boxMin: u.boxMin, boxMax: u.boxMax, steps: u.steps, useSceneDepth: config?.useSceneDepth ?? true,
  });
  material.steps = config?.steps ?? 24;
  // Per-pixel, per-frame jitter of the first step (banding → noise TAA/blur can take).
  material.offsetNode = hash(screenUV.x.add(screenUV.y.mul(7.13)).mul(4096).add(time.fract().mul(31)));
  material.premultipliedAlpha = true;
  material.blending = THREE.NormalBlending;
  material.transparent = true;
  material.depthWrite = false;
  material.depthTest = false;
  material.side = THREE.BackSide;

  const mesh = new THREE.Mesh(new THREE.BoxGeometry(size[0], size[1], size[2]), material);
  mesh.name = 'shadowed-light-volume';
  // AnalyticLightNode only sets a light's shadow up for objects that RECEIVE
  // shadows — without this the sun reaches the march unshadowed.
  mesh.receiveShadow = true;
  mesh.castShadow = false;
  mesh.renderOrder = 1; // before the additive sprites (projectiles sit at 15)
  mesh.frustumCulled = false; // the camera is usually INSIDE the box

  return {
    mesh, material,
    uniforms: u as unknown as ShadowedLightVolumeUniforms,
    setBoxWorld(center, half) {
      (u.boxMin.value as THREE.Vector3).set(center.x - half.x, center.y - half.y, center.z - half.z);
      (u.boxMax.value as THREE.Vector3).set(center.x + half.x, center.y + half.y, center.z + half.z);
    },
  };
}
